import React from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { CloudImage } from '../../../_core/_ui/cloud-image.component';
import { processTextContentForHTML } from './text-utils';

const useStyles = makeStyles((theme) => ({
  calloutContainer: {
    display: 'flex',
    padding: theme.spacing(3),
    borderRadius: theme.spacing(2),
    backgroundColor: '#f5f5f0',
    border: '2px solid #e0e0db',
    margin: theme.spacing(2, 0),
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      padding: theme.spacing(2),
      margin: theme.spacing(1, 0),
    },
  },
  leftContent: {
    marginRight: theme.spacing(3),
    flex: 1,
    flexShrink: 0,
    [theme.breakpoints.down('sm')]: {
      marginRight: 0,
      marginBottom: theme.spacing(2),
      width: '100%',
    },
  },
  rightContent: {
    marginLeft: theme.spacing(3),
    minWidth: 0,
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
      marginTop: theme.spacing(2),
      width: '100%',
    },
  },
  centerContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
  },
  imageContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  textBlock: {
    lineHeight: 1.6,
    overflowWrap: 'break-word',
    wordWrap: 'break-word',
    wordBreak: 'break-word',
    maxWidth: '100%',
    // Markdown-style formatting
    '& strong': {
      fontWeight: 700,
      color: '#333',
    },
    '& em': {
      fontStyle: 'italic',
      color: '#555',
    },
    '& .primary': {
      color: '#1976d2',
      fontWeight: 600,
    },
    '& .secondary': {
      color: '#666',
      fontSize: '0.9em',
    },
    '& .large': {
      fontSize: '1.2em',
      fontWeight: 600,
    },
    '& .small': {
      fontSize: '0.8em',
    },
  },
}));

interface MediaCalloutProps {
  // Layout configuration
  layout?: 'image-left' | 'image-right' | 'text-left' | 'text-right';
  alignment?: 'flex-start' | 'center' | 'flex-end';

  // Content configuration
  leftContent?: {
    type: 'image' | 'text';
    assetPath?: string;
    text?: string;
    width?: number;
    height?: number;
    alt?: string;
  };

  centerContent?: {
    text: string;
    variant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'body1' | 'body2';
  };

  rightContent?: {
    type: 'image' | 'text';
    assetPath?: string;
    text?: string;
    width?: number;
    height?: number;
    alt?: string;
  };

  // Styling
  backgroundColor?: string;
  borderColor?: string;
  textColor?: string;

  // Max-width restrictions for responsive behavior
  maxWidthRestrictions?: {
    desktop?: string | number;
    tablet?: string | number;
    mobile?: string | number;
    default?: string | number;
  };

  // Interaction
  onClick?: () => void;

  // Schema props for variable substitution
  data?: any;
  baseData?: any;
  player?: IPlayer;
  playerAssessment?: IPlayerAssessment;
}

/**
 * Generic media callout component for report cards
 * Supports flexible layouts with images and markdown-formatted text
 */
export const MediaCallout: React.FC<MediaCalloutProps> = ({
  alignment = 'center',
  leftContent,
  centerContent,
  rightContent,
  backgroundColor = '#f5f5f0',
  borderColor = '#e0e0db',
  textColor = '#333',
  maxWidthRestrictions,
  onClick,
  // Schema props for variable substitution
  data,
  baseData,
  player,
  playerAssessment,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Determine max-width based on device type and restrictions
  const getMaxWidth = () => {
    if (!maxWidthRestrictions) return undefined;
    
    if (isMobile && maxWidthRestrictions.mobile !== undefined) {
      return maxWidthRestrictions.mobile;
    }
    if (isTablet && !isMobile && maxWidthRestrictions.tablet !== undefined) {
      return maxWidthRestrictions.tablet;
    }
    if (!isTablet && maxWidthRestrictions.desktop !== undefined) {
      return maxWidthRestrictions.desktop;
    }
    return maxWidthRestrictions.default;
  };

  const maxWidth = getMaxWidth();

  const renderMarkdownText = (text: string) => {
    // Process variables, links and line breaks first
    let processedText = processTextContentForHTML(text, data, baseData, player, playerAssessment);
    
    // Apply additional markdown formatting
    processedText = processedText
      // Bold: **text** or __text__
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/__(.*?)__/g, '<strong>$1</strong>')
      // Italic: *text* or _text_
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/_(.*?)_/g, '<em>$1</em>')
      // Custom classes: [text](class:className) - but only if not already processed as a link
      .replace(/\[([^\]]+)\]\(class:([^)]+)\)/g, '<span class="$2">$1</span>');

    return processedText;
  };

  const renderContent = (content: any) => {
    if (!content) return null;

    if (content.type === 'image') {
      // On mobile, override prescribed widths to be responsive
      const imageWidth = isMobile ? '100%' : (content.width || 100);
      const imageHeight = isMobile ? 'auto' : (content.height || 100);
      
      return (
        <Box className={classes.imageContainer} style={{ width: '100%' }}>
          <CloudImage
            assetPath={content.assetPath || ''}
            alt={content.alt || 'Image'}
            width={imageWidth}
            height={imageHeight}
            loadingText="Loading image..."
            style={{ maxWidth: '100%', height: 'auto' }}
          />
        </Box>
      );
    }

    if (content.type === 'text' || content.text) {
      const textContent = content.text || content;
      const processedText = typeof textContent === 'string' ? renderMarkdownText(textContent) : textContent;

      return (
        <Typography
          variant={isMobile ? 'body2' : (content.variant || 'body1')}
          className={classes.textBlock}
          style={{ 
            color: textColor,
            fontSize: isMobile ? '0.875rem' : undefined,
            lineHeight: isMobile ? 1.4 : 1.6
          }}
          dangerouslySetInnerHTML={{ __html: processedText }}
        />
      );
    }

    return null;
  };

  const containerStyle = {
    backgroundColor,
    borderColor,
    alignItems: alignment,
    maxWidth,
  };

  return (
    <Box
      className={classes.calloutContainer}
      style={containerStyle}
      onClick={onClick}
    >
      {!!leftContent && (
        <Box 
          className={classes.leftContent}
          style={{ 
            // On mobile, ignore prescribed widths and use full width
            width: isMobile ? '100%' : (leftContent.width ? `${leftContent.width}px` : 'auto'),
            maxWidth: isMobile ? '100%' : undefined
          }}
        >
          {renderContent(leftContent)}
        </Box>
      )}

      {!!centerContent && (
        <Box 
          className={classes.centerContent}
          style={{ 
            width: isMobile ? '100%' : undefined,
            maxWidth: isMobile ? '100%' : undefined
          }}
        >
          {renderContent(centerContent)}
        </Box>
      )}

      {!!rightContent && (
        <Box 
          className={classes.rightContent}
          style={{ 
            // On mobile, ignore prescribed widths and use full width
            width: isMobile ? '100%' : (rightContent.width ? `${rightContent.width}px` : 'auto'),
            maxWidth: isMobile ? '100%' : undefined
          }}
        >
          {renderContent(rightContent)}
        </Box>
      )}
    </Box>
  );
};
